import React, { useMemo, type ChangeEvent } from 'react';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'RowType'. Did you mean to use 'import RowType from "@atlaskit/dynamic-table"' instead?
import type { HeadType, RowType } from '@atlaskit/dynamic-table';
import { Field } from '@atlaskit/form';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import { JiraIcon } from '@atlaskit/logo';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { useIntl } from '@atlassian/jira-intl';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../common/constants';
import type { ImportType } from '../../../common/constants/import-type';
import type { CSVUserEmail, UserMappingMap } from '../../../common/types';
import { AsyncMappingTable } from '../../../common/ui/mapping-table';
import { useContentForCSV } from '../../../common/utils/get-content-for-csv';
import messages from './messages';

const useTableHead = ({
	numberOfUsers,
	numberOfImportedUsers,
	importType,
}: {
	numberOfUsers: number;
	numberOfImportedUsers: number;
	importType?: ImportType;
}) => {
	const { formatMessage } = useIntl();
	const { usersMappingScreenColumnHeader } = useContentForCSV(importType);
	const tableHead = useMemo<HeadType>(
		() => ({
			cells: [
				{
					key: 'csvUser',
					content: usersMappingScreenColumnHeader(numberOfUsers),
					width: 30,
				},
				{
					key: 'arrow',
					width: 10,
				},
				{
					key: 'JiraUser',
					content: (
						<Inline space="space.050" alignBlock="center">
							<JiraIcon label="" size="small" appearance="brand" />
							{formatMessage(messages.importedUser, {
								numberOfUsers: numberOfImportedUsers,
							})}
						</Inline>
					),
				},
			],
		}),
		[formatMessage, numberOfImportedUsers, numberOfUsers, usersMappingScreenColumnHeader],
	);

	return tableHead;
};

const useTableRows = (
	users: UserMappingMap | null,
	onChange: (userKey: string, userValue: string) => void,
) => {
	const { formatMessage } = useIntl();
	const tableRows = useMemo<RowType[] | undefined>(
		() =>
			users
				? Object.values(users).map((user: CSVUserEmail, index: number) => ({
						key: `row-${user.userKey}`,
						cells: [
							{
								key: user.userKey,
								content: user.userKey,
							},
							{
								key: `${user.userKey}-arrow`,
								content: <ArrowRightIcon size="medium" label="Arrow Right" />,
							},
							{
								key: `${user.userKey}-email`,
								content: (
									<Field name={`${index}-email`}>
										{({ fieldProps }) => (
											<Box xcss={textInputWrapperStyles}>
												<Textfield
													type="email"
													{...fieldProps}
													aria-label={formatMessage(messages.textFieldLabel, {
														name: user.userKey,
													})}
													placeholder={formatMessage(messages.textFieldPlaceholder)}
													isCompact
													value={user.userValue || ''}
													onChange={(event: ChangeEvent<HTMLInputElement>) =>
														onChange(user.userKey, event.target.value)
													}
												/>
											</Box>
										)}
									</Field>
								),
							},
						],
					}))
				: [],
		[formatMessage, onChange, users],
	);

	return tableRows;
};

export const UserMappingTable = ({
	isLoading,
	users,
	importType,
	onChange,
}: {
	isLoading?: boolean;
	users: UserMappingMap | null;
	importType?: ImportType;
	onChange: (userKey: string, userValue: string) => void;
}) => {
	const tableHead = useTableHead({
		numberOfUsers: users ? Object.keys(users).length : 0,
		numberOfImportedUsers: users
			? Object.values(users).filter((user) => !!user.userValue).length
			: 0,
		importType,
	});

	const tableRows = useTableRows(users, onChange);

	return (
		<AsyncMappingTable
			head={tableHead}
			rows={tableRows}
			rowsPerPage={DEFAULT_TABLE_ROWS_PER_PAGE}
			emptyMessage={messages.noUsers}
			isLoading={isLoading}
		/>
	);
};

const textInputWrapperStyles = xcss({
	marginTop: 'space.negative.100',
});
